import { useEffect, useRef, useState } from 'react'

// Detects whether contentRef's natural (full) height exceeds containerRef's
// rendered (clipped) height - i.e. whether an overflow:hidden container is
// actually clipping its content right now. Attach containerRef to the
// outer, fixed/max-height + overflow:hidden element and contentRef to the
// unclipped element wrapping the real content inside it.
//
// Re-checks on every real size change via ResizeObserver (content growing
// or shrinking, the container resizing, fonts/images loading) instead of
// only once on mount/update, which is what made react-overflow (the
// library this replaces) unreliable - it relied on a legacy invisible
// <object type="text/html"> resize trick that's flaky in modern browsers,
// with nothing to ever recheck if that trick missed a later size change.
const useOverflowDetection = () => {
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return undefined

    const checkOverflow = () => {
      setIsOverflowing(content.scrollHeight > container.clientHeight)
    }

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(container)
    observer.observe(content)
    checkOverflow()

    return () => observer.disconnect()
  }, [])

  return { containerRef, contentRef, isOverflowing }
}

export default useOverflowDetection
